import logger from '@/utils/logger';
import globals from '@/globals';
import Validator from './json-valid-3k';

const { I } = globals;

export function isProduction() {
  return process.env.NODE_ENV === 'production';
}

export function isTesting() {
  return process.env.NODE_ENV === 'test';
}

/**
 * Zips arrays of the same length.
 * Example:
 * a = [1, 2, 3]
 * b = ['a', 'b', 'c']
 * zip(a, b) => [[1, "a"], [2, "b"], [3, "c"]]
 */
export function zip(a, b) {
  return a.map((e, i) => [e, b[i]]);
}

/**
 * Creates a function that negates the result of the predicate func.
 * See: https://lodash.com/docs/4.17.15#negate
 * @param {*} predicate
 * @returns
 */
export function negate(predicate) {
  return function (...args) {
    return !predicate.apply(this, args);
  };
}
/*
 * Provides a way to hook into the old Imperia/jQuery world
 * @param {*} hookFn ($) => {}
 */
export function imperiaHook(hookFn) {
  if (I.jQuery && typeof I.jQuery === 'function') {
    I.jQuery(hookFn);
  }
}

export function clamp(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

export function isValidTimestamp(t) {
  return t === new Date(t).getTime();
}

export function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d);
}

/**
 * Filters the unique values from:
 * 1. an arbitrary number of literals
 * 2. one or more arrays of literals
 * Does not work with complex data structures
 * String|Number|Array<String|Number>
 * @returns {array}
 */
export function filterUnique(...args) {
  return [].concat(...args)
    .filter((val, idx, arr) => arr.indexOf(val) === idx);
}

const tagNameRegex = /[,|\\/<>"']/;
export function isValidateTagName(string) {
  return !tagNameRegex.test(string);
}

/**
 * Validates an object based on a schema
 * @param {object} payload - pick a first level criteria.
 * @param {object} schema - an array of objects
 * @returns {Object}
 */
export function validatePayload(payload, schema) {
  Object.assign(window, { Validator });

  try {
    return Validator.validate(payload, schema);
  } catch (err) {
    if (!isTesting()) logger.warn(err);
    return { valid: false, errors: err };
  }
}

// ############### FROM UTILS - START ###############
/**
 * Checking for the existing of things. NOTE: null is considered non-existing by this method
 * @param val
 * @returns {boolean}
 */
export function is(val) {
  return val !== undefined && val !== null;
}

/**
 * Checking if something is an Element / DOM node
 * @param {*} val
 * @returns {boolean}
 */
export function isElement(val) {
  return val instanceof Element;
}

/**
 * Checking if something is a Number
 * @param {*} val
 * @returns {boolean}
 */
export function isNumber(val) {
  return typeof val === 'number';
}

/**
 * @param {String} str
 * @param {Boolean} [checkEmpty] - optional
 * @returns {boolean}
 */
export function isString(str, checkEmpty = false) {
  const match = typeof str === 'string';
  return !checkEmpty ? match : match && !!str.length;
}

/**
* @param {*} val
* @param {Boolean} [checkEmpty] - optional check whether the object has any values
* @returns {Boolean}
*/
export function isObject(val, checkEmpty) {
  try {
    const isOb = typeof val === 'object' && !Array.isArray(val) && val !== null;
    return checkEmpty === true
      ? isOb && !!Object.keys(val).length
      : isOb;
  } catch (err) {
    return false;
  }
}
/**
 * Transforms an object's values to params for an URI-like string
 * @param {object} options
 * @returns {string}
 */
export function transformOptions(options = {}) {
  const keys = Object.keys(options);
  const { length } = keys;
  const result = length ? '?' : '';
  return keys.reduce((sum, key, idx) => {
    const item = String(options[key]);
    const amp = idx >= length - 1 ? '' : '&';
    return `${sum}${key}=${item + amp}`;
  }, result);
}

/**
 * Shows a toast in the UI using the Imperia I.Status API
 * TODO: refactor toast as a component
 */
function _showToast(text, err = 0) {
  try {
    I.Status.push(text, err, { duration: 3 });
    return true;
  } catch (error) {
    if (isProduction()) {
      logger.warn(error);
    } else {
      logger.log('### TOAST DEV FALLBACK', text);
    }
  }

  return false;
}

// Shows a Toast, but guards against unwanted error messages
export function showToast(text = '', error = 0) {
  // temporary fix for ICMS-3794:

  // skip error messages that are resulting from internal errors
  // it is probably better to filter out error messages in the upper API layer
  // however, fixing ICMS-3794 here makes sure to catch all use cases with the current implementation
  // TODO: revisit/refactor the current `fetch-her` usage and error handling

  // Note: the original `error` object is swallowed by `fetch-her`
  // we have access only to the error message (not the original error object)
  // so we check for the `NetworkError` signature by checking the message contents
  // (error messages are not localized so it partially safe to do that)
  if (Boolean(error) && text.includes('NetworkError')) return;

  _showToast(text, error);
}

/**
 * Sort an array of objects by any first level criteria.
 * Optional deeper sorting could be added later.
 * @param {string} type - pick a first level criteria.
 * @param {array<object>} list - an array of objects
 * @returns {array<object>}
 */
export function sortArrayBy(type, list) {
  // Sorting seems to be a bit more complex than it should.
  // But there's a reason. We don't want to mess with the order...
  // of items that do not meet our criteria at all.
  return list.sort((a, b) => {
    const itemA = a[type];
    const itemB = b[type];

    if (!is(itemA) || !is(itemB) || itemA === itemB) {
      return 0;
    }

    return itemA > itemB ? 1 : -1;
  });
}

/**
 * This is the comparator function for natural order, and can be used
 * in conjunction with the built-in array.sort method to arrange elements
 * in ascending order.
 * @param {*} a
 * @param {*} b
 * @returns Returns -1 if a is less than b, or 1 if a is greater than b, or 0.
 */
export function ascending(a, b) {
  /* eslint-disable no-nested-ternary */
  return a == null || b == null ? NaN : a < b ? -1 : a > b ? 1 : a >= b ? 0 : NaN;
  /* eslint-enable no-nested-ternary */
}

/**
 * Similar to Array.prototype.filter but for objects.
 * Returns a new object where each of the object values
 * satisfies the given predicate.
 * Does not mutate the source object.
 */
export function filterByValue(predicate, source) {
  return Object.fromEntries(
    Object.entries(source)
      .filter(([, value]) => predicate(value)),
  );
}

// https://lodash.com/docs/4.17.15#keyBy
// https://ramdajs.com/docs/#indexBy
export function keyBy(transform, array) {
  return array.reduce((acc, o, i) => ({
    ...acc,
    [transform(o, i)]: o,
  }), {});
}

// https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string/10420404
/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh; // eslint-disable-line
    u += 1;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u]; // eslint-disable-line
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
/**
 * Format timestamp as date in the given locale.
 *
 * @param timestamp UTS seconds.
 * @param locale Specify the language for formatting (en-US)
 * @param options Additional parameters for styling the data string (07/07/20, 1:31:55 PM)
 *
 * @return Formatted string.
 */
export function formatDateLocale(timestamp, locale = 'en-US', options = { timeStyle: 'medium', dateStyle: 'short' }) {
  if (typeof timestamp !== 'number') return null;
  // Create a new JavaScript Date object based on the timestamp
  // multiplied by 1000 so that the argument is in milliseconds, not seconds.
  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat(locale, options).format(date);
}
